import {noop} from '@benchling/js-utils';
import React, {useState} from 'react';
import isEqual from 'react-fast-compare';

import {assertAndContinue} from 'Asserts';
import {Owner} from 'auth/PermissionConstants';
import BenchlingConfig from 'BenchlingConfig';
import notifications from 'notifications';
import {BaseFieldTableProps, ClientSchemaField} from 'registry/field/FieldTable';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {Tab, TabContainer} from 'ui-library/TabContainer';
import {withLoadingScreen} from 'util/Promises';

import {ConnectionSchema, ConnectionSchemaSpec} from './ConnectionSchemaConstants';
import {updateConnectionSchema} from './ConnectionSchemaDAO';
import ConnectionSchemaMetadataSettings, {getSpecForRequest} from './ConnectionSchemaMetadataSettings';
export enum Tabs {
  CONNECTION_SCHEMA_EDITOR = 'CONNECTION_SCHEMA_EDITOR',
}

export interface ExistingConnectionSchemaEditorProps {
  schemaId?: string;
  schema: ConnectionSchema;
  // Human-readable summaries of dependent computed fields, lookup fields, etc
  schemaFieldDependentsSummariesById: BaseFieldTableProps['schemaFieldDependentsSummariesById'];
  ownerChoices: Array<Owner> | null;
  refetch: () => void;
}

export default function ExistingConnectionSchemaEditor(
  props: ExistingConnectionSchemaEditorProps
): JSX.Element {
  const [activeTab, setActiveTab] = useState<Tabs>(Tabs.CONNECTION_SCHEMA_EDITOR);

  const handleFieldsChange = (newFields: Array<ClientSchemaField>): void => {
    const fieldsWithoutCid = newFields.map((field) => {
      const {cid, ...fieldWithoutCid} = field;
      return fieldWithoutCid;
    }) as Array<SchemaField>; // TODO(BNCH-30720) Fix SchemaField/ClientSchemaField type issues

    if (isEqual(fieldsWithoutCid, props.schema.allFields)) {
      return;
    }
    const spec = getSpecForRequest(newFields, props.schema.prefix);
    updateSchema({spec});
  };

  const handlePrefixChange = (newPrefix: string): void => {
    const spec = getSpecForRequest(props.schema.allFields, newPrefix);
    updateSchema({spec});
  };

  const handleNameChange = (schemaName: string): void => {
    updateSchema({schemaName});
  };

  const handleSqlIdentifierChange = (sqlIdentifier: string): void => {
    updateSchema({sqlIdentifier});
  };

  const updateSchema = (args: {
    schemaName?: string;
    sqlIdentifier?: string;
    spec?: ConnectionSchemaSpec;
  }): void => {
    withLoadingScreen(
      (async () => {
        await updateConnectionSchema({
          schemaId: props.schema.id,
          schemaName: args.schemaName ?? props.schema.name,
          sqlIdentifier:
            BenchlingConfig.SCHEMAS_ENABLE_CHANGE_WAREHOUSE_NAME && args.sqlIdentifier != null
              ? args.sqlIdentifier
              : null,
          spec: args.spec ?? getSpecForRequest(props.schema.allFields, props.schema.prefix),
        });
        props.refetch();
        notifications.success('Updated schema successfully');
      })(),
      'Updating...'
    );
  };

  return (
    <TabContainer
      activeTab={activeTab}
      onRequestTabChange={(tab: Tabs) => {
        setActiveTab(tab);
      }}
      className="u-flex-grow"
    >
      <Tab tabKey={Tabs.CONNECTION_SCHEMA_EDITOR} title="Connection Schema Settings">
        <ConnectionSchemaMetadataSettings
          adapterId={props.schema.integrationAdapterId}
          setAdapterId={(adapterId) => {
            assertAndContinue(false, 'Adapter ID cannot be changed after creation');
          }}
          name={props.schema.name}
          setName={handleNameChange}
          ownerId={props.schema.organization.id}
          setOwnerId={noop}
          sqlIdentifier={props.schema.sqlIdentifier}
          setSqlIdentifier={handleSqlIdentifierChange}
          idPrefix={props.schema.prefix}
          setIdPrefix={handlePrefixChange}
          fields={props.schema.allFields}
          setFields={handleFieldsChange}
          shouldAllowSchemaUpdates={false}
          schemaId={props.schemaId}
          schema={props.schema}
          schemaFieldDependentsSummariesById={props.schemaFieldDependentsSummariesById}
          ownerChoices={props.ownerChoices}
        />
      </Tab>
    </TabContainer>
  );
}
